import type { GetRequestsListResponse } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { useQuery } from "@tanstack/react-query";

import { mapSortColumnToBackend } from "@/features/requests/utils/sortingUtils";
import apiClient from "@/lib/api/client";

import type { DropdownFilterType, ToggleFilterType } from "../types";

interface UseProjectRequestsParams {
  cursor?: string;
  dropdownFilter: DropdownFilterType;
  enabled?: boolean;
  limit?: number;
  page?: number;
  projectId: number;
  search?: string;
  sortDescriptor?: SortDescriptor;
  toggleFilter: ToggleFilterType;
}

const mapToggleToCategory = (
  filter: ToggleFilterType,
): string[] | undefined => {
  switch (filter) {
    case "graphic": {
      return ["graphic"];
    }
    case "video": {
      return ["video"];
    }
    case "webui": {
      return ["website_design", "website_implementation"];
    }
    default: {
      return undefined;
    }
  }
};

export function useProjectRequests({
  cursor,
  dropdownFilter,
  enabled = true,
  limit = 10,
  page = 1,
  projectId,
  search,
  sortDescriptor,
  toggleFilter,
}: UseProjectRequestsParams) {
  return useQuery({
    enabled: !!projectId && enabled,
    gcTime: 15 * 60 * 1000,
    queryFn: async () => {
      const isQueuedFilter = dropdownFilter === "queued";

      const queryParams: Record<string, number | string | string[]> = {
        direction: sortDescriptor
          ? sortDescriptor.direction === "descending"
            ? "desc"
            : "asc"
          : "desc",
        projectId,
        sortBy: sortDescriptor
          ? mapSortColumnToBackend(String(sortDescriptor.column))
          : "created_at",
        tab: dropdownFilter,
      };

      if (isQueuedFilter) {
        queryParams.queueSection = "inQueue";
      } else {
        queryParams.limit = limit;
        queryParams.skipCount = "false";

        if (cursor) {
          queryParams.cursor = cursor;
        } else {
          queryParams.page = page;
        }
      }

      const category = mapToggleToCategory(toggleFilter);

      if (category) {
        queryParams.category = category;
      }

      if (search) {
        queryParams.search = search;
      }

      return await apiClient
        .url("/requests/list")
        .query(queryParams)
        .get()
        .json<GetRequestsListResponse>();
    },
    queryKey: [
      "project-requests",
      projectId,
      dropdownFilter,
      toggleFilter,
      search,
      page,
      limit,
      sortDescriptor?.column,
      sortDescriptor?.direction,
      cursor,
    ],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 60 * 1000 : false,
    staleTime: 30 * 1000,
  });
}
